// ═══════════════════════════════════════════════════
//  Metal Base component template cost
//
//  A metal base is a run of fabricated metal priced by the linear foot. Linear
//  feet come from Width; Depth and Height are recorded for spec and drawings
//  and do not enter the cost.
//
//  Unlike every other component type, this calculation takes no library
//  lookups — a metal base's cost is entirely self-contained in its own config.
//  See docs/superpowers/specs/2026-08-04-cria-metal-base-template-design.md
// ═══════════════════════════════════════════════════

import { findMetalFinish, type MetalType } from '../enums/metal-base';

/** The persisted shape of `line_items.metal_base_config`. Inches. */
export interface MetalBaseConfig {
  product_type: 'metal_base';
  width: number;
  depth: number;
  height: number;
  cost_per_lf: number;
  metal_type: MetalType;
  /** A finish key valid for `metal_type`. Never a display label. */
  finish: string | null;
  /** Meaningful only for a painted, cost-bearing finish. Null otherwise. */
  paint_cost_per_lf: number | null;
}

export interface MetalBaseCostResult {
  linear_feet: number;
  metal_cost: number;
  paint_cost: number;
  total_cost: number;
  /** Null when there is no length to divide by. */
  rate_per_lf: number | null;
  /** Warnings, joined with a space. Undefined when everything resolved cleanly. */
  note?: string;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function calcMetalBaseCostPreview(config: MetalBaseConfig): MetalBaseCostResult {
  const notes: string[] = [];

  const linear_feet = num(config?.width) / 12;
  if (linear_feet <= 0) {
    notes.push('Width is zero, so this base has no linear footage to price.');
  }

  const metal_cost = linear_feet * num(config?.cost_per_lf);

  // Anything that is not explicitly painted is stainless. A config written by
  // an older build, or hand-edited, must not silently become painted.
  const metalType: MetalType = config?.metal_type === 'painted' ? 'painted' : 'stainless';
  const finish = findMetalFinish(metalType, config?.finish ?? null);

  if (config?.finish && !finish) {
    notes.push('The saved finish is not a recognised option for this metal type.');
  }

  // Stainless finishes are descriptive only. Reading paint_cost_per_lf outside
  // this branch would let a stale value from a previous edit inflate the total.
  let paint_cost = 0;
  if (metalType === 'painted' && finish?.requiresCost) {
    const paintPerLf = num(config?.paint_cost_per_lf);
    if (paintPerLf <= 0) {
      notes.push('No paint cost per linear foot has been entered for this finish.');
    }
    paint_cost = linear_feet * paintPerLf;
  }

  const total_cost = metal_cost + paint_cost;

  return {
    linear_feet,
    metal_cost,
    paint_cost,
    total_cost,
    rate_per_lf: linear_feet > 0 ? total_cost / linear_feet : null,
    note: notes.length ? notes.join(' ') : undefined,
  };
}
